import { useEffect, useRef } from "react";
import { ExternalLink, X } from "lucide-react";
import type { ModelRelease, TimelineRelease } from "../types/model";
import { useI18n } from "../hooks/use-i18n";
import {
  type CardFlight,
  cardFlight,
  collapseToCard,
  expandFromCard,
  prefersReducedMotion,
  recedeCards,
  restoreCards,
  settleCard,
  type SharedElement,
  shiftCardFocus
} from "../lib/card-expand";
import { localizeDetailValue } from "../lib/detail-value-localization";
import { formatCount, formatReleaseDate } from "../lib/format";
import { gsap, useGSAP } from "../lib/gsap";
import type { OrganizationIndex } from "../lib/organizations";
import type {
  ReleaseFilters,
  ReleaseTagFilterInteraction,
  ReleaseTagFilterKey
} from "../lib/query-models";
import { groupMembersFiltered } from "../lib/release-groups";
import { sourceAnchorHost } from "../lib/source-anchor";
import { ClampedText } from "./ClampedText";
import { DeveloperAttribution } from "./DeveloperAttribution";
import { ModelCard } from "./ModelCard";
import { VendorMark } from "./VendorMark";

interface GroupDialogProps {
  entry: TimelineRelease;
  organizations: OrganizationIndex;
  filters: ReleaseFilters;
  onToggleFilter: (
    key: ReleaseTagFilterKey,
    value: string,
    interaction?: ReleaseTagFilterInteraction
  ) => void;
  onOpenMember: (record: ModelRelease) => void;
  onClose: () => void;
  /** Where the panel grows from and folds back into: the timeline's group card. */
  origin: () => Element | null;
  /**
   * The member whose detail now fills this window. While one is open the
   * other members step back behind it, and Escape belongs to the detail.
   */
  activeMemberId: string | null;
}

/**
 * What the group card and the panel's header both show, card side first:
 * the mark, the group's name, the developer and the group tag travel
 * between the two. The card's member count has no place of its own in the
 * header, and a developer the header shows another way has none either.
 */
const SHARED_WITH_CARD: Array<[string, string, SharedElement["fit"]]> = [
  [".vendor-mark", ".vendor-mark", "box"],
  [".model-card__title-wrap strong", "#group-title", "text"],
  [".model-card__developer", ".developer-attribution__content", "text"],
  [".pill--group", ".pill--group", "box"]
];

function flightWith(card: Element | null, dialog: HTMLElement): CardFlight | undefined {
  return cardFlight(
    card,
    dialog.querySelector(".detail-dialog__header"),
    SHARED_WITH_CARD,
    ".model-card__count, .pill--new"
  );
}

/**
 * A release group's panel: the shared introduction once in the header, then
 * every member as the card the timeline shows, dated. A member opens its own
 * detail in this same window, grown out of its card.
 */
export function GroupDialog({
  entry,
  organizations,
  filters,
  onToggleFilter,
  onOpenMember,
  onClose,
  origin,
  activeMemberId
}: GroupDialogProps) {
  const { language, t } = useI18n();
  const view = entry.groupView!;
  const { group, members, matchedIds } = view;
  const scope = useRef<HTMLDivElement>(null);
  const scrim = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const closing = useRef(false);
  const closeRequest = useRef<() => void>(() => undefined);
  const stacked = useRef(false);
  const lastActive = useRef<HTMLElement | null>(null);

  useGSAP(
    (_context, contextSafe) => {
      closing.current = false;
      let settling: gsap.core.Timeline | null = null;
      const requestClose = contextSafe!(() => {
        if (closing.current) return;
        closing.current = true;
        // Closed before the card has settled: the panel never showed.
        if (settling && settling.progress() < 1) {
          settling.kill();
          onClose();
          return;
        }
        if (prefersReducedMotion() || !dialog.current || !scrim.current) {
          onClose();
          return;
        }
        gsap.set(scope.current, { pointerEvents: "none" });
        const card = origin();
        // A pointer resting on the card would give the card its hover pose
        // the moment the panel lets the pointer through, moving the mark and
        // the name away from where the folding panel lands its own. The card
        // takes the pointer again once the panel has landed.
        if (card) gsap.set(card, { pointerEvents: "none" });
        gsap
          .timeline({
            onComplete: () => {
              if (card) gsap.set(card, { clearProps: "pointerEvents" });
              onClose();
            }
          })
          .add(
            collapseToCard(
              dialog.current,
              card?.getBoundingClientRect(),
              flightWith(card, dialog.current)
            ),
            0
          )
          .to(scrim.current, { autoAlpha: 0, duration: 0.3, ease: "power1.out" }, 0.2);
      });
      closeRequest.current = requestClose;

      const open = contextSafe!(() => {
        if (!dialog.current || !scrim.current) return;
        const card = origin();
        gsap
          .timeline()
          // The page dims while the sheet grows rather than first, so the
          // sheet leaves the card with the card's border still standing
          // out against the page.
          .to(scrim.current, { autoAlpha: 1, duration: 0.5, ease: "power1.inOut" }, 0.06)
          .add(
            expandFromCard(
              dialog.current,
              card?.getBoundingClientRect(),
              flightWith(card, dialog.current)
            ),
            0
          );
        dialog.current.focus({ preventScroll: true });
      });

      if (!prefersReducedMotion() && dialog.current && scrim.current) {
        // The panel waits, unseen, while the card straightens up and scrolls
        // into full view. The clear shade still takes the pointer meanwhile,
        // so the card is not leaned again nor the list scrolled away.
        gsap.set([dialog.current, scrim.current], { autoAlpha: 0 });
        settling = settleCard(origin(), [scrim.current]);
        if (settling) {
          gsap.set(scrim.current, { visibility: "visible" });
          settling.eventCallback("onComplete", open);
        } else {
          open();
        }
      } else {
        dialog.current?.focus({ preventScroll: true });
      }
      return () => {
        closeRequest.current = () => undefined;
      };
    },
    { scope }
  );

  // Escape closes the panel only while no member detail sits in the window;
  // over a detail it belongs to the detail, which folds back into its card.
  // A plain effect, because useGSAP keeps its context, and so this listener,
  // when its dependencies change, and the panel would close under the detail.
  useEffect(() => {
    if (activeMemberId) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeRequest.current();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeMemberId]);

  // A member opening steps the rest back; its detail closing brings them
  // forward, rippling out from the member that was open.
  useGSAP(
    () => {
      const body = dialog.current?.querySelector<HTMLElement>(".group-members");
      if (!body || prefersReducedMotion()) {
        stacked.current = Boolean(activeMemberId);
        return;
      }
      const cards = gsap.utils.toArray<HTMLElement>(
        ":scope > .model-card-shell",
        body
      );
      const active = activeMemberId
        ? body.querySelector<HTMLElement>(
            `[data-model-id="${CSS.escape(activeMemberId)}"]`
          )
        : null;
      if (activeMemberId && !stacked.current) {
        stacked.current = true;
        recedeCards(cards, active);
      } else if (active && stacked.current && active !== lastActive.current) {
        // The detail's previous and next buttons moved to a sibling.
        shiftCardFocus(lastActive.current, active);
      } else if (!activeMemberId && stacked.current) {
        stacked.current = false;
        restoreCards(cards, lastActive.current);
      }
      if (active) lastActive.current = active;
    },
    { dependencies: [activeMemberId] }
  );

  const first = members[0]!;
  const last = members[members.length - 1]!;
  const matched = new Set(matchedIds);
  const partial = groupMembersFiltered(view);
  const locale = language === "zh" ? "zh-CN" : "en";

  return (
    <div ref={scope} className="group-dialog-scope">
      <div
        ref={scrim}
        className="detail-scrim"
        role="presentation"
        onClick={(event) => {
          if (event.target === event.currentTarget) closeRequest.current();
        }}
      />
      <article
        ref={dialog}
        className="detail-dialog group-dialog"
        data-lenis-prevent
        role="dialog"
        aria-modal="true"
        aria-labelledby="group-title"
        aria-hidden={activeMemberId ? true : undefined}
        tabIndex={-1}
      >
        <header className="detail-dialog__header">
          <div className="detail-heading">
            <div className="detail-identity">
              <VendorMark
                company={first.company}
                modelName={first.name}
                modelFamily={first.family}
                organizationLogoCompany={organizations.get(first.organization.primaryId)?.logoCompany}
                large
              />
              <div className="detail-identity__content">
                <DeveloperAttribution record={first} organizations={organizations} />
                <div className="detail-identity__title-row">
                  <h1 id="group-title">{group.name}</h1>
                  {group.officialUrl ? (
                    <a
                      className="source-anchor source-anchor--official"
                      href={group.officialUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${t("primarySource")} · ${sourceAnchorHost(group.officialUrl)} (${t("opensInNewTab")})`}
                    >
                      <span>{t("primarySource")}</span>
                      <ClampedText as="strong" text={sourceAnchorHost(group.officialUrl)} />
                      <ExternalLink size={10} aria-hidden="true" />
                    </a>
                  ) : null}
                </div>
              </div>
              <p className="detail-identity__summary">
                {group.summary
                  ? localizeDetailValue("modelGroup.summary", group.summary, language)
                  : t("releaseGroup")}
              </p>
            </div>
            <div className="detail-lead">
              <span className="release-pills">
                <span className="pill pill--group">
                  <span>{t("releaseGroup")}</span>
                </span>
                <span className="pill pill--group-count">
                  <span>{formatCount(members.length, "model", language)}</span>
                </span>
              </span>
            </div>
          </div>

          <div className="detail-header-side">
            <div className="detail-nav">
              <button
                type="button"
                className="detail-close"
                onClick={() => closeRequest.current()}
                aria-label={t("closeGroup")}
              >
                <span>Esc</span>
                <X size={15} aria-hidden="true" />
              </button>
            </div>
            <dl className="detail-date-summary">
              <div>
                <dt>{t("firstRelease")}</dt>
                <dd>{formatReleaseDate(first, false, language)}</dd>
              </div>
              <div>
                <dt>{t("groupModels")}</dt>
                <dd>{members.length.toLocaleString(locale)}</dd>
              </div>
              <div>
                <dt>{t("latestRelease")}</dt>
                <dd>{formatReleaseDate(last, false, language)}</dd>
              </div>
            </dl>
          </div>
        </header>

        <div className="detail-dialog__body group-dialog__body">
          <section className="detail-card group-members-card">
            <h2>
              {t("groupModels")} · {members.length.toLocaleString(locale)}
              {partial ? (
                <small className="group-members-card__hint">
                  {matched.size}/{members.length} {t("groupMatchHint")}
                </small>
              ) : null}
            </h2>
            <div className="group-members model-grid">
              {members.map((member) => (
                <ModelCard
                  key={member.id}
                  record={member}
                  organizations={organizations}
                  showDate
                  isNew={false}
                  keyboardActive={false}
                  onOpen={onOpenMember}
                  filters={filters}
                  onToggleFilter={onToggleFilter}
                  emphasis={partial ? (matched.has(member.id) ? "match" : "dim") : undefined}
                />
              ))}
            </div>
          </section>
        </div>
      </article>
    </div>
  );
}
